import { useState, useEffect } from 'react';
import { SettingsSubNav } from '../components/SettingsSubNav';
import { MapPin, CheckCircle2 } from 'lucide-react';

export function LocationsSettingsPage() {
  const [locations, setLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [facilityType, setFacilityType] = useState('branch');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');

  const fetchLocations = () => {
    setLoading(true);
    fetch('/api/accounting/org/locations')
      .then((res) => res.json())
      .then((data) => {
        if (data.ok || data.success) {
          setLocations(Array.isArray(data.data) ? data.data : []);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/accounting/org/locations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, name, facilityType, city, state }),
      });
      const data = await res.json();
      if (res.ok && (data.ok || data.success)) {
        setShowModal(false);
        setCode('');
        setName('');
        setToast('New facility / location added successfully!');
        setTimeout(() => setToast(null), 3000);
        fetchLocations();
      } else {
        alert(data.error?.message || 'Failed to create location.');
      }
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div className="text-slate-900 dark:text-slate-100">
      <SettingsSubNav />

      <div className="max-w-[1580px] mx-auto space-y-6 pb-16">
        {toast && (
          <div className="p-4 bg-success/10 dark:bg-success border border-success/25 dark:border-success/40 rounded-xl text-success-text dark:text-success-text text-sm flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-success-text" />
            {toast}
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold">Locations & Facilities</h2>
            <p className="text-xs text-slate-500">Manage warehouses, branches, stores, plants, production units, tanks, and virtual transit locations.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { type: 'headquarters', label: '+ Head Office' },
              { type: 'warehouse', label: '+ Warehouse' },
              { type: 'branch', label: '+ Branch' },
              { type: 'store', label: '+ Store' },
              { type: 'plant', label: '+ Plant' },
              { type: 'tank_bin', label: '+ Tank' },
              { type: 'transit', label: '+ Transit Location' },
            ].map((btn) => (
              <button
                key={btn.type}
                onClick={() => {
                  setFacilityType(btn.type);
                  setShowModal(true);
                }}
                className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 font-medium text-xs rounded-lg shadow-xs transition"
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Loading locations...</div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 font-semibold text-slate-900 dark:text-slate-100 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Facility Name</th>
                  <th className="px-4 py-3">Facility Type</th>
                  <th className="px-4 py-3">City / State</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {locations.map((loc) => (
                  <tr key={loc._id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                    <td className="px-4 py-3 font-mono font-bold text-slate-900 dark:text-slate-100">{loc.code}</td>
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-indigo-500" />
                      {loc.name}
                    </td>
                    <td className="px-4 py-3 capitalize text-xs font-medium text-slate-600 dark:text-slate-400">
                      {loc.facilityType?.replace('_', ' ')}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {loc.city ? `${loc.city}, ${loc.state}` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-success/10 text-success-text dark:bg-success dark:text-success-text">
                        Active
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <h3 className="text-lg font-bold">Add Facility / Location</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Code *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. WH-01"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm font-mono uppercase"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Facility Type</label>
                  <select
                    value={facilityType}
                    onChange={(e) => setFacilityType(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                  >
                    <option value="headquarters">Headquarters</option>
                    <option value="branch">Branch</option>
                    <option value="warehouse">Warehouse</option>
                    <option value="store">Store / POS Outlet</option>
                    <option value="service_office">Service Office</option>
                    <option value="plant">Plant / Factory</option>
                    <option value="production_unit">Production Unit</option>
                    <option value="tank_bin">Tank / Storage Bin</option>
                    <option value="transit">Virtual Goods In Transit</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Facility Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Central Fulfillment Hub"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">City</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">State / Province</label>
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 hover:underline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs rounded-lg shadow-sm"
                >
                  Create Facility
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default LocationsSettingsPage;
